/**
 * User-friendly message shown when APIs are unreachable (e.g. connection refused, Failed to fetch).
 * Used instead of raw "Failed to fetch" or "TypeError: Failed to fetch" in the UI.
 */
export const FETCH_ERROR_USER_MESSAGE =
  'System is temporarily down. Please check again after sometime.';

/**
 * Returns true if the error indicates a network/fetch failure (no response from server).
 * Covers: Failed to fetch, TypeError from fetch, ERR_CONNECTION_REFUSED, network error, etc.
 */
export function isNetworkOrFetchError(error: unknown): boolean {
  if (!error) return false;
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message: unknown }).message)
      : String(error);
  const lower = message.toLowerCase();
  return (
    lower.includes('failed to fetch') ||
    lower.includes('network error') ||
    lower.includes('network request failed') ||
    lower.includes('err_connection_refused') ||
    lower.includes('err_connection_reset') ||
    lower.includes('load failed') ||
    (lower.includes('typeerror') && lower.includes('fetch'))
  );
}

/**
 * Converts API/fetch errors into a user-friendly message for display.
 * For network/fetch failures (e.g. server down, connection refused), returns a friendly
 * "system temporarily down" message. For other errors, returns the original message or fallback.
 *
 * @param error - The caught error (Error instance or unknown)
 * @param fallback - Optional fallback when error has no message (e.g. "Failed to load data")
 * @returns User-facing error message
 */
export function getFetchErrorMessage(
  error: unknown,
  fallback: string = 'Something went wrong. Please try again.'
): string {
  if (isNetworkOrFetchError(error)) {
    return FETCH_ERROR_USER_MESSAGE;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return fallback;
}
